/**
 * Centralized error reporting utility.
 *
 * Every catch block in the app calls `reportError()` instead of
 * `Sentry.captureException()` directly.  If we ever swap Sentry for
 * another provider, we only change THIS file — zero changes in components.
 */
import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ErrorSeverity = 'fatal' | 'error' | 'warning' | 'info';

interface ErrorContext {
  /** Which part of the app: 'editor', 'auth', 'generation', 'credits', etc. */
  module: string;
  /** Any extra key-value pairs to attach */
  extra?: Record<string, unknown>;
}

// ---------------------------------------------------------------------------
// Core API
// ---------------------------------------------------------------------------

/**
 * Single entry point for all error reporting.
 * Swap the implementation here to change provider for the entire app.
 */
export function reportError(
  error: unknown,
  context: ErrorContext,
  severity: ErrorSeverity = 'error',
): void {
  // Always keep console.error for local dev visibility
  console.error(`[${context.module}]`, error);

  Sentry.withScope((scope) => {
    scope.setLevel(severity);
    scope.setTag('module', context.module);
    if (context.extra) {
      scope.setExtras(context.extra);
    }
    Sentry.captureException(
      error instanceof Error ? error : new Error(String(error)),
    );
  });
}

// ---------------------------------------------------------------------------
// User context helpers
// ---------------------------------------------------------------------------

/** Call on login — includes subscription tier for prioritisation. */
export function setUserContext(user: {
  uid: string;
  subscription: string;
  email?: string;
}): void {
  Sentry.setUser({ id: user.uid });
  Sentry.setTag('subscription_tier', user.subscription);
}

/** Call on logout. */
export function clearUserContext(): void {
  Sentry.setUser(null);
}
